import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { getAccessToken } from '../agent/keycloakAuth';
import { getMockAuthLogin, getMockAuthRoles, isLocalAuthBypassEnabled } from '../config/mockAuth';
import { RemusPlatform, getActivePlatform } from '../platformConfig';
import { errorMessage } from '../behavior/messages';

type PathParams = Record<string, string | number | boolean | undefined> | false | undefined;
type QueryParams = Record<string, unknown> | false | undefined;
export type ApiRequestOptions = {
  suppressGlobalError?: boolean;
  /**
   * Aborts the request. Pass React Query's `signal` so a superseded fetch (a new
   * time range, a changed filter, leaving the page) stops server-side work
   * instead of running to completion.
   */
  signal?: AbortSignal;
  /** Per-request timeout in ms; without one axios waits forever. */
  timeout?: number;
};

export type ApiHandler = {
  _axios: AxiosInstance;
  get: <T = any>(
    url: string,
    pathParams?: PathParams,
    params?: QueryParams,
    options?: ApiRequestOptions
  ) => Promise<{ data: T }>;
  post: <T = any>(
    url: string,
    data?: unknown,
    pathParams?: PathParams,
    params?: QueryParams,
    options?: ApiRequestOptions
  ) => Promise<{ data: T }>;
  put: <T = any>(
    url: string,
    data?: unknown,
    pathParams?: PathParams,
    params?: QueryParams,
    options?: ApiRequestOptions
  ) => Promise<{ data: T }>;
  patch: <T = any>(
    url: string,
    data?: unknown,
    pathParams?: PathParams,
    params?: QueryParams,
    options?: ApiRequestOptions
  ) => Promise<{ data: T }>;
  delete: <T = any>(
    url: string,
    pathParams?: PathParams,
    params?: QueryParams,
    options?: ApiRequestOptions
  ) => Promise<{ data: T }>;
  pdf: (url: string, pathParams?: PathParams, params?: QueryParams) => Promise<void>;
};

const replacePathParams = (url: string, pathParams?: PathParams) => {
  if (!pathParams) {
    return url;
  }
  return Object.entries(pathParams).reduce(
    (path, [key, value]) => path.replace(`{${key}}`, encodeURIComponent(String(value ?? ''))),
    url
  );
};

const toAxiosConfig = (
  params?: QueryParams,
  options?: ApiRequestOptions
): AxiosRequestConfig & ApiRequestOptions => ({
  ...(params ? { params } : {}),
  ...options,
});

const paramsSerializer = { indexes: null };

const filenameFromContentDisposition = (header: unknown, fallback: string): string => {
  if (typeof header !== 'string') {
    return fallback;
  }

  const utf8Match = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (utf8Match) {
    return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
  }

  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  return asciiMatch ? asciiMatch[1].trim() : fallback;
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

const createApiHandler = (config: AxiosRequestConfig): ApiHandler => {
  const axiosInstance = axios.create({
    ...config,
    paramsSerializer: config.paramsSerializer ?? paramsSerializer,
  });
  return {
    _axios: axiosInstance,
    get: (url, pathParams, params, options) =>
      axiosInstance.get(replacePathParams(url, pathParams), toAxiosConfig(params, options)),
    post: (url, data, pathParams, params, options) =>
      axiosInstance.post(replacePathParams(url, pathParams), data, toAxiosConfig(params, options)),
    put: (url, data, pathParams, params, options) =>
      axiosInstance.put(replacePathParams(url, pathParams), data, toAxiosConfig(params, options)),
    patch: (url, data, pathParams, params, options) =>
      axiosInstance.patch(replacePathParams(url, pathParams), data, toAxiosConfig(params, options)),
    delete: (url, pathParams, params, options) =>
      axiosInstance.delete(replacePathParams(url, pathParams), toAxiosConfig(params, options)),
    pdf: async (url, pathParams, params) => {
      const response = await axiosInstance.get(replacePathParams(url, pathParams), {
        ...toAxiosConfig(params),
        responseType: 'blob',
      });

      const filename = filenameFromContentDisposition(
        response.headers?.['content-disposition'],
        'report.pdf'
      );

      triggerBlobDownload(response.data as Blob, filename);
    },
  };
};

const installServerErrorInterceptor = (handler: ApiHandler) => {
  const axiosInstance = handler._axios;
  axiosInstance.interceptors.response.use(
    response => response,
    (error: AxiosError) => {
      const status = error?.response?.status;
      const suppressGlobalError = (error.config as ApiRequestOptions | undefined)
        ?.suppressGlobalError;
      if (typeof status === 'number' && status >= 500 && !suppressGlobalError) {
        errorMessage({ title: 'Server error', error });
      }
      return Promise.reject(error);
    }
  );
};

// Single source of truth for the Authorization header on every outgoing
// request:
//   - In local mock-auth (DEV + VITE_LOCAL_AUTH_BYPASS=true), strip
//     Authorization and send ADFS_LOGIN + DEBUG_ROLES so REMUS's backend
//     DevSecurityConfig authenticates us without a JWT roundtrip.
//   - Otherwise, ask keycloak for a fresh access token via getAccessToken()
//     and inject it as a Bearer header.
const installAuthInterceptor = (handler: ApiHandler) => {
  const axiosInstance = handler._axios;
  axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const headers: any = config.headers ?? {};
    if (isLocalAuthBypassEnabled()) {
      delete headers.Authorization;
      headers.ADFS_LOGIN = getMockAuthLogin();
      headers.DEBUG_ROLES = getMockAuthRoles();
    } else {
      try {
        const token = await getAccessToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Auth provider chose to bail (e.g. triggered a login redirect).
        // Let the request continue; the backend will 401 if needed.
      }
    }
    config.headers = headers;
    return config;
  });
};

const apiTest: ApiHandler = createApiHandler({
  baseURL: '/api/TEST',
});
installServerErrorInterceptor(apiTest);
installAuthInterceptor(apiTest);

const apiProd: ApiHandler = createApiHandler({
  baseURL: '/api/PROD',
});
installServerErrorInterceptor(apiProd);
installAuthInterceptor(apiProd);

const apiConfig = {
  baseURL: '/api/PROD',
};

// One API adapter per platform. Selecting the backend is a pure function of an
// explicit platform — no hidden window.location read lives here any more.
const handlersByPlatform: Record<RemusPlatform, ApiHandler> = {
  PROD: apiProd,
  TEST: apiTest,
};

export function getApiHandler(platform: RemusPlatform): ApiHandler {
  return handlersByPlatform[platform] ?? apiProd;
}

// Resolve the API adapter for a platform. Callers that already hold the platform
// (e.g. a React hook that read it from the route) should pass it explicitly —
// that makes the dependency visible and the call unit-testable. When omitted, it
// falls back to the active platform from the single source of truth.
export function getAPI(platform: RemusPlatform = getActivePlatform()): ApiHandler {
  return getApiHandler(platform);
}

// Backwards-compatible accessor for the active platform, kept for non-React
// callers such as STOMP managers. It now delegates to the single source of
// truth instead of re-parsing the URL.
export function getCurrentPlatform(): RemusPlatform {
  return getActivePlatform();
}

export default {
  getAPI,
  getApiHandler,
  apiProd,
  apiTest,
  apiConfig,
  getCurrentPlatform,
};
